import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogTitle,
  Icons,
  MOBILE_SHEET_EASE_IN,
} from '@continuum/ui';
import {useBodyClassName} from '@continuum/ui/hooks';
import {cva, type VariantProps} from 'class-variance-authority';
import type {ReactNode} from 'react';

export type CanvasPanelView = 'chat' | 'pages';

// One shell for both views, so switching never animates two competing panels.
const canvasPanelVariants = cva(
  'absolute flex flex-col bg-background overflow-hidden transition-transform duration-300 ease-in-out',
  {
    variants: {
      variant: {
        floating:
          'right-2 top-2 bottom-2 w-[calc(var(--canvas-panel-width)-16px)] rounded-2xl border border-border shadow-xl',
        flush:
          'inset-y-0 right-0 w-(--canvas-panel-width) border-l border-border',
      },
      state: {
        open: 'translate-x-0',
        closed: '',
      },
    },
    compoundVariants: [
      // Inset 8px with a shadow, so it travels past its own gutter.
      {
        variant: 'floating',
        state: 'closed',
        class: 'translate-x-[calc(100%+1rem)]',
      },
      {variant: 'flush', state: 'closed', class: 'translate-x-full'},
    ],
    defaultVariants: {variant: 'floating', state: 'open'},
  },
);

interface CanvasPanelProps extends VariantProps<typeof canvasPanelVariants> {
  open: boolean;
  className?: string;
  children: ReactNode;
}

export const CanvasPanel = ({
  open,
  variant,
  className,
  children,
}: CanvasPanelProps) => (
  <div
    className={cn(
      'fixed bottom-0 right-0 top-0 z-40 overflow-hidden transition-[width] duration-300 ease-in-out',
      open ? 'w-(--canvas-panel-width)' : 'w-0',
      className,
    )}
    aria-hidden={!open}
    {...(!open && {inert: true})}
  >
    <div
      className={canvasPanelVariants({
        variant,
        state: open ? 'open' : 'closed',
      })}
    >
      {children}
    </div>
  </div>
);

interface CanvasPanelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  cssVars?: Record<string, string>;
  children: ReactNode;
}

// Opaque, not a Sheet: a dark scrim reads as internal chrome. cssVars are
// re-applied inside because the Dialog portals out of their scope.
export const CanvasPanelDialog = ({
  open,
  onOpenChange,
  title,
  cssVars,
  children,
}: CanvasPanelDialogProps) => {
  useBodyClassName(
    cn('bg-background! transition-colors duration-200', MOBILE_SHEET_EASE_IN),
    open,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        position="mobile-sheet"
        overlay="mobile-sheet"
        className="max-w-none"
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="flex min-h-0 flex-1 flex-col" style={cssVars}>
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface CanvasPanelHeaderProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  closeLabel: string;
  actions?: ReactNode;
}

export const CanvasPanelHeader = ({
  title,
  subtitle,
  onClose,
  closeLabel,
  actions,
}: CanvasPanelHeaderProps) => (
  <div className="shrink-0 px-4 py-3 sm:px-5 sm:pt-5">
    <div className="flex items-center gap-2">
      <div className="size-1.75 shrink-0 rounded-full bg-(--canvas-primary)" />
      <h2 className="font-serif text-xl font-normal text-foreground">
        {title}
      </h2>
      <div className="ml-auto flex items-center gap-1">
        {actions}
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          aria-label={closeLabel}
          tooltip="Close"
          tooltipOptions={{side: 'bottom'}}
          className="rounded-full text-muted-foreground"
        >
          <Icons.x className="size-5" />
        </Button>
      </div>
    </div>
    {subtitle && (
      <p className="mt-2 text-[13px] leading-normal text-muted-foreground">
        {subtitle}
      </p>
    )}
  </div>
);
